import { styled } from '@mui/material/styles';
import { Checkbox } from 'components/ui/Checkbox';
import { TextLink } from 'components/ui/TextLink';

export const DialogSearchableMultiSelectRoot = styled('div')(({ theme }) => ({
  color: theme.palette.neutral[600],
  display: 'grid',
  fontSize: 'var(--dialog-description-font-size)',
  fontWeight: theme.typography.fontWeightMedium,
  gap: theme.spacing(1),
  lineHeight: 'var(--dialog-description-line-height)',
  minWidth: 0,
}));

export const DialogSearchableMultiSelectCheckbox = styled(Checkbox)({
  pointerEvents: 'none',
});

export const DialogSearchableMultiSelectField = styled('div')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.common.white,
  border: `var(--dialog-border-width) solid ${theme.palette.neutral[300]}`,
  borderRadius: 'var(--dialog-select-trigger-radius)',
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'space-between',
  minHeight: 'var(--dialog-select-trigger-height)',
  minWidth: 0,
  padding: theme.spacing(1),
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: '100%',
  '&:hover': {
    backgroundColor: theme.palette.neutral[50],
    borderColor: theme.palette.brand[400],
  },
  '&:focus-visible': {
    backgroundColor: theme.palette.brand[100],
    borderColor: theme.palette.brand[400],
    outline: 0,
  },
  '&[aria-disabled="true"]': {
    backgroundColor: theme.palette.action.disabledBackground,
    borderColor: theme.palette.neutral[200],
    color: theme.palette.action.disabled,
    cursor: 'not-allowed',
  },
}));

export const DialogSearchableMultiSelectPlaceholder = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[500],
  fontSize: 'var(--dialog-description-font-size)',
  fontWeight: 400,
  lineHeight: 'var(--dialog-description-line-height)',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const DialogSearchableMultiSelectChevron = styled('span')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.neutral[500],
  display: 'inline-flex',
  flex: '0 0 auto',
  height: 'var(--dialog-select-trigger-icon-size)',
  justifyContent: 'center',
  width: 'var(--dialog-select-trigger-icon-size)',
  '& .MuiSvgIcon-root': {
    fontSize: 'var(--dialog-select-trigger-icon-size)',
  },
}));

export const DialogSearchableMultiSelectValues = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: '1 1 auto',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'visible',
}));

export const DialogSearchableMultiSelectChip = styled('span', {
  shouldForwardProp: prop => prop !== 'chipTone',
})<{ chipTone: 'field' | 'list' }>(({ chipTone, theme }) => ({
  alignItems: 'center',
  backgroundColor: chipTone === 'field' ? theme.palette.neutral[100] : theme.palette.common.white,
  border: `var(--dialog-border-width) solid ${
    chipTone === 'field' ? theme.palette.neutral[400] : theme.palette.neutral[200]
  }`,
  borderRadius: 'var(--dialog-select-trigger-radius)',
  boxSizing: 'border-box',
  color: chipTone === 'field' ? theme.palette.neutral[600] : theme.palette.neutral[500],
  display: 'inline-flex',
  fontSize: chipTone === 'field' ? '12px' : 'var(--dialog-description-font-size)',
  fontWeight: 400,
  gap: theme.spacing(0.5),
  lineHeight: chipTone === 'field' ? '16px' : 'var(--dialog-description-line-height)',
  maxWidth: '100%',
  minWidth: 0,
  padding: chipTone === 'field' ? theme.spacing(0.25, 0.5) : theme.spacing(0.5, 1),
}));

export const DialogSearchableMultiSelectChipLabel = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const DialogSearchableMultiSelectChipRemoveButton = styled('button')(({ theme }) => ({
  alignItems: 'center',
  background: 'transparent',
  border: 0,
  borderRadius: theme.shape.radius.xs,
  color: theme.palette.neutral[500],
  cursor: 'pointer',
  display: 'inline-flex',
  flex: '0 0 auto',
  height: theme.spacing(2),
  justifyContent: 'center',
  padding: 0,
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: theme.spacing(2),
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.brand[200],
    color: theme.palette.neutral[900],
    outline: 0,
  },
  '&:disabled': {
    color: theme.palette.action.disabled,
    cursor: 'not-allowed',
  },
  '& .MuiSvgIcon-root': {
    fontSize: theme.spacing(1.5),
  },
}));

export const DialogSearchableMultiSelectClearButton = styled('button')(({ theme }) => ({
  alignItems: 'center',
  background: 'transparent',
  border: 0,
  borderRadius: theme.shape.radius.xs,
  color: theme.palette.neutral[500],
  cursor: 'pointer',
  display: 'inline-flex',
  flex: '0 0 auto',
  height: 'var(--dialog-select-trigger-icon-size)',
  justifyContent: 'center',
  padding: 0,
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: 'var(--dialog-select-trigger-icon-size)',
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.neutral[100],
    color: theme.palette.neutral[900],
    outline: 0,
  },
  '& .MuiSvgIcon-root': {
    fontSize: theme.spacing(1.5),
  },
}));

export const DialogSearchableMultiSelectAddButton = styled(TextLink)(({ theme }) => ({
  appearance: 'none',
  background: 'transparent',
  border: 0,
  borderRadius: theme.shape.radius.xs,
  color: theme.palette.brand[600],
  cursor: 'pointer',
  gap: theme.spacing(0.5),
  fontSize: 'var(--dialog-description-font-size)',
  fontWeight: theme.typography.fontWeightMedium,
  justifySelf: 'start',
  lineHeight: 'var(--dialog-description-line-height)',
  padding: theme.spacing(0.5),
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: 'fit-content',
  '&::after': {
    display: 'none',
  },
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.neutral[100],
    color: theme.palette.brand[700],
    outline: 0,
  },
  '&:disabled, &[aria-disabled="true"]': {
    color: theme.palette.action.disabled,
    cursor: 'not-allowed',
  },
  '& .MuiSvgIcon-root': {
    fontSize: theme.spacing(2),
  },
}));
